import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Tooltip,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import ShieldIcon from '@mui/icons-material/Shield';
import TierBadge from './TierBadge';
import RatingDelta from './RatingDelta';
import StreakFlame from './StreakFlame';
import { tierColor } from './tierColors';
import { RankedProfile } from '../../types/ranked';

export interface RankedProfilePanelProps {
  profile: RankedProfile;
  /** Delta of the most recent rating event. Omit when unknown. */
  latestDelta?: number | null;
  /**
   * Whether the copy addresses the student ("Your standing") or describes them
   * ("Ava's standing"). Anything but the student's own page must pass 'third'.
   */
  voice?: 'second' | 'third';
  /** Preferred name for third-person copy; falls back to the profile's own. */
  subjectName?: string;
}

/**
 * A student's ranked standing — the shared card behind their own page, a
 * parent's child detail and a teacher's student detail.
 *
 * Shared rather than copied so the three cannot drift, and because the copy is
 * the whole difficulty: this markup previously lived only on the student's own
 * page and said "Your standing" about whoever's ID was in the URL.
 *
 * The rating is always visible. The ranked leaderboard already publishes every
 * student's rating and the post-quiz reveal animates it, so hiding it here
 * would only make a student's own profile the least informative view of
 * themselves. What protects this age group from a falling number is the tier
 * floor, not secrecy — the tier stays the headline, the rating stays muted.
 */
const RankedProfilePanel: React.FC<RankedProfilePanelProps> = ({
  profile,
  latestDelta,
  voice = 'second',
  subjectName
}) => {
  const theme = useTheme();
  const color = tierColor(profile.tier.name);

  const isSecond = voice === 'second';
  const name = subjectName ?? profile.displayName ?? 'This mathlete';

  const heading = isSecond ? 'Your standing' : `${possessive(name)} standing`;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={profile.visibility === 'full' ? 7 : 12}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            position: 'relative',
            overflow: 'hidden',
            height: '100%',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: color
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase',
                color: 'text.secondary'
              }}
            >
              {heading}
            </Typography>

            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <TierBadge
                tier={profile.tier.name}
                label={profile.tier.label}
                size="large"
                showLabel={false}
              />
              <Box>
                <Typography variant="h4" fontWeight={700} sx={{ color, lineHeight: 1.15 }}>
                  {profile.tier.label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography
                    sx={{
                      fontFamily: 'monospace',
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}
                  >
                    {profile.rating.value} SR
                  </Typography>
                  {latestDelta != null && <RatingDelta delta={latestDelta} />}
                </Box>
              </Box>
            </Box>

            {/*
              Season position. Deliberately a bare "3rd of 42" — it gives a
              parent the relative standing they will ask for first without
              naming any other family's child.
            */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {profile.rank != null
                ? `${ordinal(profile.rank)} of ${profile.totalRanked} ranked mathletes`
                : 'Not ranked yet — needs a live quiz with 3 or more mathletes'}
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2" color="text.secondary">
                  {profile.tier.nextThreshold ? 'Progress to the next rank' : 'Top of the ladder'}
                </Typography>
                {profile.tier.nextThreshold != null && (
                  <Typography variant="body2" fontWeight={600}>
                    {profile.tier.nextThreshold - profile.rating.value} SR to go
                  </Typography>
                )}
              </Box>
              <LinearProgress
                variant="determinate"
                value={profile.tier.progress * 100}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: alpha(color, 0.12),
                  '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 5 }
                }}
              />
            </Box>

            {profile.tier.heldByFloor && (
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  color: 'text.secondary'
                }}
              >
                <ShieldIcon sx={{ fontSize: 18, mt: '2px' }} />
                <Typography variant="body2">
                  <strong>{capitalise(profile.tier.name)} floor active.</strong>{' '}
                  {isSecond
                    ? `Your rating can move within ${capitalise(
                        profile.tier.name
                      )}, but you cannot drop out of a rank you have reached.`
                    : `${name}’s rating can move within ${capitalise(
                        profile.tier.name
                      )}, but they cannot drop out of a rank they have reached.`}
                </Typography>
              </Box>
            )}

            {profile.rating.isProvisional && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                {isSecond
                  ? 'Your rank is still settling — play a few more quizzes and it will find its level quickly.'
                  : `${name}’s rank is still settling — a few more quizzes and it will find its level.`}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/*
        The effort track exists only under full visibility. XP and streaks are
        private: the leaderboard never published them, so a peer must not see
        them here either. Narrowing the union is what enforces that — the
        fields are not merely hidden, they are absent from the type.
      */}
      {profile.visibility === 'full' && (
        <Grid item xs={12} lg={5}>
          <Card
            elevation={0}
            sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  color: 'text.secondary'
                }}
              >
                Season effort
              </Typography>

              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <StarIcon sx={{ color: theme.palette.secondary.main }} />
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{
                    fontFamily: 'monospace',
                    fontVariantNumeric: 'tabular-nums',
                    color: theme.palette.secondary.dark
                  }}
                >
                  {profile.xp.season.toLocaleString()}
                </Typography>
                <Typography color="text.secondary" fontWeight={600}>
                  XP
                </Typography>
              </Box>

              <Tooltip title="XP never decreases. A bad quiz still pays.">
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Never goes down
                </Typography>
              </Tooltip>

              <Box sx={{ mt: 2.5 }}>
                <StreakFlame streak={profile.streak} />
              </Box>

              <Box sx={{ mt: 2.5, display: 'flex', gap: 3 }}>
                <Stat label="Quizzes" value={profile.rating.gamesPlayed} />
                <Stat label="Peak" value={profile.rating.peak} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

const Stat: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <Box>
    <Typography
      variant="h6"
      fontWeight={700}
      sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** "Ava" → "Ava’s"; "Chris" → "Chris’". */
function possessive(name: string): string {
  return name.endsWith('s') ? `${name}’` : `${name}’s`;
}

function ordinal(n: number): string {
  const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
  return `${n}${suffix}`;
}

export default RankedProfilePanel;
